import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Col, Container, Form, Row } from "reactstrap";
import { PasswordInput, Text, TextInput, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSessionStorageState } from "ahooks";
import * as yup from "yup";

import { ContactUsForm } from "@/components/ContactUsForm/ContactUsForm";
import { ThemedButton } from "@/components/ThemedButton";

import { clearMessage } from "@/actions/message";

import ContactUs2 from "@/views/pages/learner/course/ContactUs2";
import { logGTMUser } from "@/utilities/googleTagManager";

import github from "@/assets/img/icons/common/github.svg";
import google from "@/assets/img/icons/common/google.svg";

import { login } from "../../../actions/auth";
import styles from "./Login.module.scss";
import ModalFirstTimeResetPassword from "./ModalFirstTimeResetPassword";

const schema = yup
	.object({
		email: yup.string().email().required(),
		password: yup.string().required(),
	})
	.required();

function Login() {
	let navigate = useNavigate();
	const pageProps = useOutletContext();
	console.log("pageProps", pageProps);

	const [passwordType, setPasswordType] = useState("password");
	const [loading, setLoading] = useState(false);
	const [modalOpen, setmodalOpen] = React.useState(false);

	const { user: currentUser } = useSelector((state) => state.auth);
	const { message } = useSelector((state) => state.message);
	// required to keep track the 'ts_ccc' value, so that it can be used in the referral form after user successful login
	const [cachedTSCCC, setCachedTSCCC] = useSessionStorageState("ts_ccc", {
		defaultValue: null,
	});

	const hasOpend = useRef(false);
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	const hasContactUs = params.get("contactus");
	const enquiryModalMetadata = {
		ts_ccc: params.get("ts_ccc"),
		contactus: params.get("contactus"),
	};

	const handleContactUs = () => {
		const modelId = modals.open({
			title: "Contact Us",
			size: "xl",
			children: (
				<ContactUsForm
					onSuccess={() => {
						modals.close(modelId);
					}}
					metadata={enquiryModalMetadata}
				/>
			),
		});
	};

	useEffect(() => {
		if (params.get("ts_ccc")) {
			setCachedTSCCC(params.get("ts_ccc"));
		}
	}, []);

	useEffect(() => {
		if (!hasOpend.current && parseInt(hasContactUs) == 1) {
			hasOpend.current = true;
			handleContactUs();
		}
	}, [hasContactUs]);

	const dispatch = useDispatch();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
	});

	const togglePassword = () => {
		if (passwordType === "password") {
			setPasswordType("text");
			return;
		}
		setPasswordType("password");
	};

	const handleLogin = (data) => {
		setLoading(true);
		dispatch(login(data.email, data.password))
			.then((data) => {
				if (data.isHyperAdminLogin) {
					navigate("/login/hyperadmin-otp-validation");
				} else {
					const loggingInUser = data?.user;
					logGTMUser(
						data.user.learnermaster_id ? data.user.learnermaster_id.toString() : "",
						data.user.learnermaster_name ? data.user.learnermaster_name.toString() : "",
						data.user.learnermaster_dob ? data.user.learnermaster_dob : "",
						data.user.learnermaster_nationality ? data.user.learnermaster_nationality : "",
						data.user.learnermaster_gender ? data.user.learnermaster_gender : "",
					);

					if (loggingInUser && !loggingInUser?.email_verified_at) {
						navigate("/login/otp-validation");
					} else if (loggingInUser && loggingInUser?.reset_password) {
						setmodalOpen(true);
					} else if (cachedTSCCC) {
						navigate("/referral");
					} else {
						const intendedUrl = localStorage.getItem("intended_url");
						if (intendedUrl) {
							navigate(intendedUrl);
							localStorage.removeItem("intended_url");
						} else {
							navigate("/");
						}
					}
				}
			})
			.catch((e) => {
				setLoading(false);
			});
	};

	React.useEffect(() => {
		dispatch(clearMessage());
	}, []);

	React.useEffect(() => {
		if (currentUser && currentUser.user.reset_password) {
			setmodalOpen(true);
		}
	});

	console.log("pageProps", pageProps);

	return (
		<>
			<Outlet />
			<Container className="">
				<div className={styles.title_container}>
					<Title
						order={2}
						lh={1}
						fz={{
							base: "1.2rem",
							md: "1.7rem",
						}}>
						If you are willing to learn
						<br />
						You are unstoppabble
					</Title>
					<Title order={3} mt={10} fz="1.2rem" c="var(--fca-green)">
						Learner's Portal
					</Title>
					<Title order={3} mt={10} visibleFrom="md">
						Sign in
					</Title>
				</div>
				{pageProps?.signButton}
				{!pageProps?.signButton && (
					<Row className="justify-content-center">
						<Col lg="12" md="12" className="mw-580px">
							<Card className="bg-transparent shadow-none border-0 mb-0">
								<CardBody className="px-lg-4 py-lg-">
									<Form id="login_form" onSubmit={handleSubmit(handleLogin)}>
										<TextInput
											size="md"
											label="Email"
											placeholder="Email"
											{...register("email")}
											error={errors.email?.message}></TextInput>

										<PasswordInput
											mt={20}
											size="md"
											label="Password"
											placeholder="Password"
											{...register("password")}
											error={errors.password?.message}></PasswordInput>

										<div className="custom-control custom-control-alternative custom-checkbox text-center d-none">
											<input
												className="custom-control-input"
												id=" customCheckLogin"
												type="checkbox"
											/>
											<label className="custom-control-label d-none" htmlFor=" customCheckLogin">
												<span className="text-muted">Remember me</span>
											</label>
										</div>
										<Link
											to="/login/reset-password"
											className="text-blue bold ml-1 fs-15"
											state={{}}>
											<Text fw={500} color="themed-black.9" td="underline" size="sm">
												Forgot Password?
											</Text>
										</Link>
										<div className="text-center mt-5">
											<ThemedButton px={100} type="submit" loading={loading}>
												Sign in
											</ThemedButton>
										</div>

										{message && (
											<div className="form-group pt-3">
												<div className="alert alert-danger" role="alert">
													{message}
												</div>
											</div>
										)}
									</Form>
								</CardBody>

								<CardHeader className="bg-transparent pb-5 d-none">
									<div className="text-center text-muted mb-4">
										<small>Or sign in with credentials</small>
									</div>
									<div className="text-muted text-center mt-2 mb-3">
										<small>Sign in with</small>
									</div>
									<div className="btn-wrapper text-center">
										<Button
											className="btn-neutral btn-icon"
											color="default"
											href="#pablo"
											onClick={(e) => e.preventDefault()}>
											<span className="btn-inner--icon mr-1">
												<img alt="..." src={github} />
											</span>
											<span className="btn-inner--text">Github</span>
										</Button>
										<Button
											className="btn-neutral btn-icon"
											color="default"
											href="#pablo"
											onClick={(e) => e.preventDefault()}>
											<span className="btn-inner--icon mr-1">
												<img alt="..." src={google} />
											</span>
											<span className="btn-inner--text">Google</span>
										</Button>
									</div>
								</CardHeader>
							</Card>
							<div className="sinup_wrap text-center pt-2">
								<div className="pdt20">
									<Text>
										Don't have an account?
										<Text component={Link} to="/register" pl={5} td="underline">
											Create new account
										</Text>
									</Text>
								</div>
								<Text mt={10}>
									Need help ?
									<Text
										span
										pl={5}
										td="underline"
										onClick={handleContactUs}
										style={{ cursor: "pointer" }}>
										Contact us
									</Text>
								</Text>
							</div>
						</Col>
					</Row>
				)}
			</Container>
			<ModalFirstTimeResetPassword modalChange={modalOpen} setModalChange={setmodalOpen} />
		</>
	);
}

export default Login;
